import { jsonrepair } from 'jsonrepair';
import { AssistantChatMessage, ChatMessage } from './ChatTypes';

const parseMessageHeader = ([chunk]: string[]) => {
    if (chunk.startsWith('f:')) {
        return {
            ...JSON.parse(chunk.slice(2)),
            consumed: [chunk],
        };
    }
    throw new Error('Invalid message header');
};

const parseTextChunkSequence = (chunks: string[]) => {

    const consumed = [] as string[];
    const buffer = [] as string[];

    for (const ch of chunks) {
        if (ch.startsWith('0:"')) {
            buffer.push(JSON.parse(ch.slice(2)));
            consumed.push(ch);
        }
        else {
            break;
        }
    }

    return {
        text: buffer.join(''),
        consumed,
    };
};

const parseToolCallFinalChunk = (chunk: string) => {

    if (!chunk.startsWith('9:')) {
        throw new Error('Invalid tool call final chunk');
    }

    const data = JSON.parse(chunk.slice(2));

    const call = {
        toolCallId: data.toolCallId,
        toolName: data.toolName,
        args: data.args,
        state: 'call',
    };

    return {
        call,
        consumed: [chunk],
    };
};

const parseToolCallChunkSequence = (chunks: string[]) => {

    // First, skip ahead and see if we can find a 9: chunk
    const index = chunks.findIndex(ch => ch.startsWith('9:'));
    if (index !== -1) {
        const result = parseToolCallFinalChunk(chunks[index]);
        return {
            call: result.call,
            consumed: chunks.slice(0, index + 1),
        };
    }

    const consumed = [] as string[];
    const argsBuffer = [] as string[];
    const call = {
        toolCallId: '',
        toolName: '',
        args: {},
        state: 'partial-call',
    };

    for (const ch of chunks) {
        const code = ch.slice(0, 2);
        const data = JSON.parse(ch.slice(2));
        if (code === 'b:') {
            call.toolCallId = data.toolCallId;
            call.toolName = data.toolName;
        }
        else if (code === 'c:') {
            argsBuffer.push(data.argsTextDelta);
            call.args = JSON.parse(jsonrepair(argsBuffer.join('')));
        }
        else {
            throw new Error('Invalid tool call delta sequence');
        }
        consumed.push(ch);
    }

    return {
        call,
        consumed,
    };
};

export const parseInboundDataStream = (data: string, initialMessageId: string): ChatMessage => {

    const message: AssistantChatMessage = {
        id: initialMessageId,
        role: 'assistant',
        content: [],
        createdAt: new Date(),
        streaming: true,
    };

    const chunks = data.split('\n');

    for (let i = 0; i < chunks.length;) {
        const ch = chunks[i];
        const future = chunks.slice(i);

        if (ch.startsWith('f:')) {
            const { messageId, consumed } = parseMessageHeader(future);
            message.id = messageId;
            i += consumed.length;
        }
        else if (ch.startsWith('0:')) {
            const { text, consumed } = parseTextChunkSequence(future);
            message.content.push({
                type: 'text',
                text,
            });
            i += consumed.length;
        }
        else if (ch.startsWith('b:')) {
            const { call, consumed } = parseToolCallChunkSequence(future);
            message.content.push({
                type: 'tool-call',
                toolCallId: call.toolCallId,
                toolName: call.toolName,
                args: call.args,
            });
            i += consumed.length;
        }
        else {
            i++;
        }
    }

    // Check last chunk for completion
    const last = chunks[chunks.length - 1];
    if (last.startsWith('e:') || last.startsWith('d:')) {
        message.streaming = false;
    }

    return message;
};

export const DataStream = Object.freeze({
    parse: (data: string, initialMessageId: string) => parseInboundDataStream(data, initialMessageId),
});
